/**
 * A set of helpers for working with strings which may or may not include code points
 * representable by a single JavaScript "string character".
 */
export class UnicodeHelper {
  /**
   * Returns true if |text| contains any code points composed of surrogate pairs.
   *
   * If this is true, it means that text.length will not equal the number of
   * decoded code points (visible characters) so we cannot rely on indices derived
   * from the range of string length and need to work against the set of decoded
   * code points instead.
   */
  private static _containsAnySurrogatePair(text: string): boolean {
    return /[\uD800-\uDBFF][\uDC00-\uDFFF]/g.test(text)
  }

  /**
   * Convert the string into an array of visible characters.
   */
  private static _toVisibleCharacters(text: string): string[] {
    return Array.from(text)
  }

  /**
   * Returns the length of |text| in visible characters which may differ
   * from text.length.
   */
  public static getLengthInVisibleCharacters(text: string): number {
    // If all the characters in |text| are plain, just use the length.
    if (!this._containsAnySurrogatePair(text)) {
      return text.length
    }

    // If there are surrogate-pair sequences, convert the string into display
    // characters and count those.
    return this._toVisibleCharacters(text).length
  }

  private static _removeCharacterPlain(text: string, index: number): string {
    return text.slice(0, index) + text.slice(index + 1)
  }

  private static _removeCharacterVisible(text: string, index: number): string {
    const characters = this._toVisibleCharacters(text)
    return characters.filter((_val, i) => i !== index).join('')
  }

  /**
   * Remove the character at |index| from |text| and return the new string.
   */
  public static removeCharacter(text: string, index: number): string {
    if (!this._containsAnySurrogatePair(text)) {
      return this._removeCharacterPlain(text, index)
    }

    return this._removeCharacterVisible(text, index)
  }

  private static _addStringVisible(text: string, str: string, index: number): string {
    const characters = this._toVisibleCharacters(text)
    const left = characters.slice(0, index)
    const right = characters.slice(index)
    return left.concat(str).concat(right).join('')
  }

  private static _addStringPlain(text: string, str: string, index: number): string {
    return text.slice(0, index) + str + text.slice(index)
  }

  /**
   * Insert |str| at |index| within |text| and return the resulting string.
   */
  public static addString(text: string, str: string, index: number): string {
    if (!this._containsAnySurrogatePair(text)) {
      return this._addStringPlain(text, str, index)
    }

    return this._addStringVisible(text, str, index)
  }

  private static _substringPlain(text: string, index: number, count: number): string {
    return text.slice(index, index + count)
  }

  private static _substringVisible(text: string, index: number, count: number): string {
    const characters = this._toVisibleCharacters(text)
    return characters.slice(index, index + count).join('')
  }

  /**
   * Split a substring of length |count| from |text| starting at |index|.
   */
  public static substring(text: string, index: number, count: number): string {
    if (!this._containsAnySurrogatePair(text)) {
      return this._substringPlain(text, index, count)
    }

    return this._substringVisible(text, index, count)
  }

  private static _getCharacterAtPlain(text: string, index: number): string {
    return text.charAt(index)
  }

  private static _getCharacterAtVisible(text: string, index: number): string {
    const characters = this._toVisibleCharacters(text)
    if (index >= characters.length) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw 'Invalid character index'
    }

    return characters[index]!
  }

  /**
   * Return the visible character in |text| at |index|.
   */
  public static getCharacterAt(text: string, index: number): string {
    if (!this._containsAnySurrogatePair(text)) {
      return this._getCharacterAtPlain(text, index)
    }

    return this._getCharacterAtVisible(text, index)
  }

  /**
   * Map a visible character |index| to the corresponding plain text index in |text|
   * and return that plain text index.
   */
  public static getPlainCharacterIndexFromVisibleCharacterIndex(text: string, index: number): number {
    // If text doesn't contain any surrogate pairs, index can be used directly.
    if (index === 0 || !this._containsAnySurrogatePair(text)) {
      return index
    }

    // Count up the plain text characters of each visible character until we reach index.
    const characters = this._toVisibleCharacters(text)
    let charCount = 0
    for (let i = 0; i < index; i++) {
      charCount += characters[i]!.length
    }

    return charCount
  }

  /**
   * Map a plain text |index| to the corresponding visible character index in |text|
   * and return that visible character index.
   */
  public static getVisibleCharacterIndexFromPlainCharacterIndex(text: string, index: number): number {
    // If text doesn't contain any surrogate pairs, index can be used directly.
    if (index === 0 || !this._containsAnySurrogatePair(text)) {
      return index
    }

    // Count up the plain text characters of each visible character until we reach index.
    const characters = this._toVisibleCharacters(text)
    let visibleCharCount = 0
    let plainCharCount = 0
    for (const char of characters) {
      plainCharCount += char.length
      if (plainCharCount > index) {
        break
      }

      visibleCharCount++
    }

    return visibleCharCount
  }

  /**
   * Returns the byte size of |text| when encoded as UTF-8.
   * This is useful for estimating network payload sizes.
   */
  public static getByteSize(text: string): number {
    return new TextEncoder().encode(text).length
  }

  /**
   * Determines whether the given text represents a newline.
   */
  public static isNewline(text?: string): boolean {
    return text === '\n' || text === '\r\n'
  }
}
